import {TStimMetadata} from "@/common/types/TStimConfig.ts";
import {IProtocolStepKind, IPortMap} from "./protocol";
import {IdType} from "@/utils/types/id.ts";
import {TAnalysisGroup} from "./analysis";
import {stepDefinitionBaseFields} from "./stepMetadataSchemas";
import {validateStepMetadata} from "./stepMetadataValidation";
import {IQuestionnaireQAndAPair} from "./questionnaire";


export type TProtocolDefinitionId = IdType<string, "TProtocolDefinitionId">
export type TProtocolStepDefinitionId = IdType<string, "TProtocolStepDefinitionId">

export interface IProtocolDefinitionType {
    id: TProtocolDefinitionId // allows us to override id in children
    steps: Array<IProtocolStepDefinition>
}

export interface IProtocolDefinitionBase {
    name: string
    instructions?: string
    metadata: {}
    createdAt: string
}

export interface IProtocolDefinition extends IProtocolDefinitionBase, IProtocolDefinitionType {
    /** only used for display purposes when displaying lots of protocol definitions and we don't
     * want to load steps for all of them but want to display step count.
     */
    readonly step_ids: Array<TProtocolStepDefinitionId>
}

/**
 * This portion of step definition is shared with all steps
 */
export interface IProtocolStepDefinitionCommonFields {
    kind: IProtocolStepKind
    name: string
    instructions?: string
    createdAt: string
}

export interface IProtocolStepDefinitionBase extends IProtocolStepDefinitionCommonFields {
    id: TProtocolStepDefinitionId
    protocolId: TProtocolDefinitionId
}

export interface IMegScanStepDefinition extends IProtocolStepDefinitionBase {
    kind: IProtocolStepKind.MEG_SCAN
    tags?: Array<string>
    duration?: number
}

export interface IEEGScanStepDefinition extends IProtocolStepDefinitionBase {
    kind: IProtocolStepKind.EEG_SCAN
    tags?: Array<string>
    duration?: number
}

export interface IECGScanStepDefinition extends IProtocolStepDefinitionBase {
    kind: IProtocolStepKind.ECG_SCAN
    tags?: Array<string>
    duration?: number
}

export interface IMeasureBloodPressureStepDefinition extends IProtocolStepDefinitionBase {
    kind: IProtocolStepKind.MEASURE_BP
    count: number;
}

export interface IPupillometryReading {
    is_left: boolean;
    captured_at?: string;
    device_time?: string;
}

export interface IMeasurePLRStepDefinition extends IProtocolStepDefinitionBase {
    kind: IProtocolStepKind.MEASURE_PLR
    count: number;
    readings: IPupillometryReading[];
}

export interface IMeasureHeartRateStepDefinition extends IProtocolStepDefinitionBase {
    kind: IProtocolStepKind.MEASURE_HR
    bpm?: number
}


export interface IStimStepDefinition extends IProtocolStepDefinitionBase {
    kind: IProtocolStepKind.STIM_EVENT
    stimMetadata?: TStimMetadata
    tags?: Array<string>
}

export interface IQuestionAnswerMetricStepDefinition extends IProtocolStepDefinitionBase {
    kind: IProtocolStepKind.Q_AND_A
    question?: string
    answer?: string
}

export interface IQuestionnaireStepDefinition extends IProtocolStepDefinitionBase {
    kind: IProtocolStepKind.QUESTIONNAIRE
    items: IQuestionnaireQAndAPair[]
}

export interface INoteStepDefinition extends IProtocolStepDefinitionBase {
    kind: IProtocolStepKind.NOTE
    note?: string
}

export interface IRestStepDefinition extends IProtocolStepDefinitionBase {
    kind: IProtocolStepKind.REST_EVENT
    duration?: number
}

export type UploaderPreset = "intan_rhs" | "hdf5" | "ant_neuro" | "custom";

export interface IDirConfig {
    id: string;
    dir_path?: string;
    recursive: boolean;
    uploader_preset?: UploaderPreset;
    after_upload?: "delete" | "backup";
    uploader_custom_extensions?: Array<{ extension: string, min_file_age: number }>;
}

export interface IStartDataCollectionStepDefinition extends IProtocolStepDefinitionBase {
    kind: IProtocolStepKind.START_DATA_COLLECTION
    data_dir_path?: string
    dir_configs?: IDirConfig[]
    aria?: {
        adapter_placement?: "Left" | "Right"
        analysisGroups: Array<TAnalysisGroup>
    }
}


export interface IStopDataCollectionStepDefinition extends IProtocolStepDefinitionBase {
    kind: IProtocolStepKind.STOP_DATA_COLLECTION
}

export interface IGroupStepDefinition extends IProtocolStepDefinitionBase {
    kind: IProtocolStepKind.GROUP
    memberStepIds: Array<TProtocolStepDefinitionId>
}

export interface IInstructionStepDefinition extends IProtocolStepDefinitionBase {
    kind: IProtocolStepKind.INSTRUCTIONS
}

export interface IVasPainScoreStepDefinition extends IProtocolStepDefinitionBase {
    kind: IProtocolStepKind.VAS_PAIN_SCORE
    score?: number
}

export interface INRSPainScoreStepDefinition extends IProtocolStepDefinitionBase {
    kind: IProtocolStepKind.NRS_PAIN_SCORE
    score?: number
}

export interface IPPReliefStepDefinition extends IProtocolStepDefinitionBase {
    kind: IProtocolStepKind.PP_RELIEF
    score?: number
}

export interface IPortConfigStepDefinition extends IProtocolStepDefinitionBase {
    kind: IProtocolStepKind.PORT_CONFIG
    count: number;
    ports: IPortMap[];
}

export type IProtocolStepDefinition =
    IMegScanStepDefinition
    | IEEGScanStepDefinition
    |
    IMeasureBloodPressureStepDefinition
    | IMeasurePLRStepDefinition
    | IMeasureHeartRateStepDefinition
    | IStimStepDefinition
    |
    IQuestionAnswerMetricStepDefinition
    | IQuestionnaireStepDefinition
    | INoteStepDefinition
    | IRestStepDefinition
    | IECGScanStepDefinition
    | IStartDataCollectionStepDefinition
    | IStopDataCollectionStepDefinition
    | IGroupStepDefinition
    | IInstructionStepDefinition
    | IVasPainScoreStepDefinition
    | INRSPainScoreStepDefinition
    | IPPReliefStepDefinition
    | IPortConfigStepDefinition;

// unique fields for each type of step
type TMegScanStepDefinitionMetadata = Omit<IMegScanStepDefinition, keyof IProtocolStepDefinitionBase>
type TEEGScanStepDefinitionMetadata = Omit<IEEGScanStepDefinition, keyof IProtocolStepDefinitionBase>
type TECGStepDefinitionMetadata = Omit<IECGScanStepDefinition, keyof IProtocolStepDefinitionBase>
type TMeasureBloodPressureStepDefinitionMetadata = Omit<IMeasureBloodPressureStepDefinition, keyof IProtocolStepDefinitionBase>
type TMeasurePLRStepDefinitionMetadata = Omit<IMeasurePLRStepDefinition, keyof IProtocolStepDefinitionBase>
type TMeasureHeartRateStepDefinitionMetadata = Omit<IMeasureHeartRateStepDefinition, keyof IProtocolStepDefinitionBase>
type TStimStepDefinitionMetadata = Omit<IStimStepDefinition, keyof IProtocolStepDefinitionBase>
type TQuestionAnswerMetricStepDefinitionMetadata = Omit<IQuestionAnswerMetricStepDefinition, keyof IProtocolStepDefinitionBase>
type TQuestionnaireStepDefinitionMetadata = Omit<IQuestionnaireStepDefinition, keyof IProtocolStepDefinitionBase>
type TNoteStepDefinitionMetadata = Omit<INoteStepDefinition, keyof IProtocolStepDefinitionBase>
type TRestStepDefinitionMetadata = Omit<IRestStepDefinition, keyof IProtocolStepDefinitionBase>
type TStartDataCollectionStepDefinitionMetadata = Omit<IStartDataCollectionStepDefinition, keyof IProtocolStepDefinitionBase>
type TStopDataCollectionStepDefinitionMetadata = Omit<IStopDataCollectionStepDefinition, keyof IProtocolStepDefinitionBase>
type TGroupStepDefinitionMetadata = Omit<IGroupStepDefinition, keyof IProtocolStepDefinitionBase>
type TInstructionStepDefinitionMetadata = Omit<IInstructionStepDefinition, keyof IProtocolStepDefinitionBase>
type TVasPainScoreStepDefinitionMetadata = Omit<IVasPainScoreStepDefinition, keyof IProtocolStepDefinitionBase>
type TNRSPainScoreStepDefinitionMetadata = Omit<INRSPainScoreStepDefinition, keyof IProtocolStepDefinitionBase>
type TPPReliefStepDefinitionMetadata = Omit<IPPReliefStepDefinition, keyof IProtocolStepDefinitionBase>
type TPortConfigStepDefinitionMetadata = Omit<IPortConfigStepDefinition, keyof IProtocolStepDefinitionBase>

//  metadata represents all fields unique to each step that are effectively stored in the Servers metadata field
export type TProtocolStepDefinitionMetadata =
    TMegScanStepDefinitionMetadata
    | TEEGScanStepDefinitionMetadata
    | TECGStepDefinitionMetadata
    | TMeasureBloodPressureStepDefinitionMetadata
    | TMeasurePLRStepDefinitionMetadata
    | TMeasureHeartRateStepDefinitionMetadata
    | TStimStepDefinitionMetadata
    | TQuestionAnswerMetricStepDefinitionMetadata
    | TQuestionnaireStepDefinitionMetadata
    | TNoteStepDefinitionMetadata
    | TRestStepDefinitionMetadata
    | TStartDataCollectionStepDefinitionMetadata
    | TStopDataCollectionStepDefinitionMetadata
    | TGroupStepDefinitionMetadata
    | TInstructionStepDefinitionMetadata
    | TVasPainScoreStepDefinitionMetadata
    | TNRSPainScoreStepDefinitionMetadata
    | TPPReliefStepDefinitionMetadata
    | TPortConfigStepDefinitionMetadata;

/**
 * Extract all of the fields that should be stored in the Servers Metadata
 * field.
 *
 * @param {IProtocolStepDefinition} step - The protocol step definition object containing detailed information about the step.
 * @returns {object} The extracted metadata object with the non-metadata properties omitted.
 */
export const extractMetadata = (step: Partial<IProtocolStepDefinition>): TProtocolStepDefinitionMetadata => {
    const metadata: Record<string, unknown> = {};

    for (const key of Object.keys(step)) {
        if (!(stepDefinitionBaseFields as readonly string[]).includes(key)) {
            metadata[key] = (step as Record<string, unknown>)[key];
        }
    }

    // Validate and return metadata (only if kind is present)
    if (step.kind) {
        return validateStepMetadata(step.kind, metadata, "extractMetadata") as TProtocolStepDefinitionMetadata;
    }
    return metadata as TProtocolStepDefinitionMetadata;
}

/**
 * Parameters for creating a protocol step definition.
 * id and createdAt are optional - if omitted, the step is intended for server-side creation.
 */
export interface ICreateProtocolStepDefinitionParams {
    kind: IProtocolStepKind;
    protocolId: TProtocolDefinitionId;
    name: string;
    id?: TProtocolStepDefinitionId;
    createdAt?: string;
    instructions?: string;
}

/**
 * Factory function to create a protocol step definition.
 *
 * When id and createdAt are provided, creates a complete step definition.
 * When omitted, creates a partial step definition for server-side creation.
 *
 * @param params - Base fields for the step definition (id and createdAt optional)
 * @param metadata - Optional metadata fields specific to the step kind
 * @returns A protocol step definition (partial if id/createdAt omitted)
 *
 * @example
 * // For server-side creation (id/createdAt generated by server):
 * const newStep = createProtocolStepDefinition({
 *     kind: IProtocolStepKind.MEG_SCAN,
 *     protocolId,
 *     name: "MEG Scan 1"
 * }, { duration: 300 });
 *
 * @example
 * // For local creation with all fields:
 * const step = createProtocolStepDefinition({
 *     kind: IProtocolStepKind.MEG_SCAN,
 *     protocolId,
 *     name: "MEG Scan 1",
 *     id: stepId,
 *     createdAt: new Date().toISOString()
 * }, { duration: 300 });
 */
export const createProtocolStepDefinition = <T extends IProtocolStepDefinition>(
    params: ICreateProtocolStepDefinitionParams,
    metadata?: Partial<Omit<T, keyof IProtocolStepDefinitionBase>>
): Partial<T> => {
    const defaultMetadata =  createDefaultStepMetadata(params.kind)
    const stepDef: Partial<IProtocolStepDefinitionBase> = {
        kind: params.kind,
        protocolId: params.protocolId,
        name: params.name,
        ...(params.id && { id: params.id }),
        ...(params.createdAt && { createdAt: params.createdAt }),
        ...(params.instructions && { instructions: params.instructions }),
    };

    return {
        ...stepDef,
        ...defaultMetadata,
        ...metadata, // override metadata with values passed in
    } as Partial<T>;
};

const createDefaultStepMetadata = (kind : IProtocolStepKind) : Partial<TProtocolStepDefinitionMetadata> => {
    switch (kind) {
        case IProtocolStepKind.MEASURE_BP:
            return { count:3 }
        case IProtocolStepKind.MEASURE_PLR:
            return { count: 2 }
        case IProtocolStepKind.PORT_CONFIG:
            return { count: 2, ports: [] }
        case IProtocolStepKind.QUESTIONNAIRE:
            return { items: [] }
        default:
            return {}
    }
}
